import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { syncService, docService, analyticsService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useDocumentTypes } from '../context/DocumentTypesContext';
import { STATUS } from '../theme/tokens';
import { apiError } from '../utils/apiError';
import {
  HEADER_RULE,
  ROW_RULE,
  STICKY_LEFT_EDGE,
  STICKY_RIGHT_EDGE,
  TABLE,
  TABLE_STYLE,
  TH,
  Z_BODY_PINNED,
  Z_HEADER,
  Z_HEADER_PINNED,
} from '../theme/table';
import {
  HiRefresh,
  HiCheckCircle,
  HiClock,
  HiDatabase,
  HiChevronLeft,
  HiChevronRight,
  HiLightningBolt,
  HiExclamationCircle,
  HiCog,
  HiReply,
} from 'react-icons/hi';

// ─── Constants ───────────────────────────────────────────────────────────────

const SOURCE_OPTIONS = [
  { value: '',              label: 'All Sources' },
  { value: 'dmz_server',   label: 'DMZ Server' },
  { value: 'h_drive',      label: 'H Drive' },
  { value: 'dynamic_ax',   label: 'Dynamic AX' },
  { value: 'manual_upload', label: 'Manual Upload' },
];

const SOURCE_LABELS = {
  dmz_server:    'DMZ Server',
  h_drive:       'H Drive',
  dynamic_ax:    'Dynamic AX',
  manual_upload: 'Manual Upload',
};

// Dot + label rather than a coloured pill: the word carries the meaning, so the
// step is still readable without relying on colour.
const STEP_BADGE = {
  pending:    { label: 'Queued',         dot: '#898781' },
  processing: { label: 'OCR',            dot: '#2a78d6' },
  completed:  { label: 'Awaiting index', dot: STATUS.serious },
  failed:     { label: 'OCR failed',     dot: STATUS.critical },
};

// Urgency tint for a queue row. Pinned cells need the SAME colour as a solid
// value — the scrolling columns pass under them, so an alpha wash would let
// that content show through.
const queueRowTone = (isStuck, isWarning) => {
  if (isStuck) return { row: 'bg-red-50 hover:bg-red-100/60', pinned: 'bg-red-50' };
  if (isWarning) return { row: 'bg-amber-50/70 hover:bg-amber-100/60', pinned: 'bg-amber-50' };
  return { row: 'hover:bg-slate-50', pinned: 'bg-white group-hover:bg-slate-50' };
};

const THRESHOLD_OPTIONS = [
  { value: 30,  label: '30 min' },
  { value: 60,  label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
];

// Same dot-plus-word treatment as the queue, from the reserved status palette.
const LOG_STATUS_DOT = {
  completed:     STATUS.good,
  failed:        STATUS.critical,
  partial_error: STATUS.warning,
  skipped:       '#898781',
};

// ─── Shared Utilities ─────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <HiChevronLeft className="w-4 h-4" />
      </button>
      <span className="px-3 py-1 text-sm text-slate-600">
        {page} / {totalPages}
      </span>
      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <HiChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

/** Parse "new=X modified=X deleted=X errors=X" into colored spans */
const SyncSummaryText = ({ text }) => {
  if (!text) return <span className="text-slate-400">-</span>;
  const parts = text.split(' ');
  return (
    <span className="flex flex-wrap gap-1.5 text-xs">
      {parts.map((part, i) => {
        const [k, v] = part.split('=');
        const num = parseInt(v, 10);
        const isError = k === 'errors' && num > 0;
        const isNew = k === 'new' && num > 0;
        return (
          <span
            key={i}
            className={
              isError ? 'text-red-600 font-semibold' :
              isNew   ? 'text-emerald-600 font-semibold' :
                        'text-slate-500'
            }
          >
            {part}
          </span>
        );
      })}
    </span>
  );
};

const formatDuration = (ms) => {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

/** Absolute timestamp: "28/04 14:35" */
const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Elapsed time since isoString, returns { text, mins } */
const calcElapsed = (iso) => {
  if (!iso) return { text: '-', mins: 0 };
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1)  return { text: '< 1 min', mins };
  if (mins < 60) return { text: `${mins} min`, mins };
  const hrs = Math.floor(mins / 60);
  return { text: `${hrs}h ${mins % 60}m`, mins };
};

// ─── RecoveryPanel (superuser only) ──────────────────────────────────────────

const RecoveryPanel = ({ onDone }) => {
  const { user } = useAuth();
  const [open, setOpen]               = useState(false);
  const [deletedAfter, setDeletedAfter] = useState('');
  const [deletedBefore, setDeletedBefore] = useState('');
  const [fixResult, setFixResult]     = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const [fixing, setFixing]           = useState(false);
  const [restoring, setRestoring]     = useState(false);

  if (!user?.is_superuser) return null;

  const handleFixOcr = async () => {
    setFixing(true);
    setFixResult(null);
    try {
      const r = await docService.fixOcrComplete();
      setFixResult({ ok: true, msg: r.message });
      onDone();
    } catch (e) {
      setFixResult({ ok: false, msg: e?.response?.data?.detail || 'Request failed.' });
    } finally {
      setFixing(false);
    }
  };

  const handleRestore = async () => {
    if (!deletedAfter) return;
    setRestoring(true);
    setRestoreResult(null);
    try {
      // date input gives "YYYY-MM-DD" — add time bounds for the API
      const afterIso  = `${deletedAfter}T00:00:00`;
      const beforeIso = deletedBefore ? `${deletedBefore}T23:59:59` : undefined;
      const r = await docService.restoreFalseDeleted(afterIso, beforeIso);
      setRestoreResult({
        ok: true,
        msg: `Restored ${r.total}: ${r.restored_completed} completed + ${r.restored_processing} queued for OCR`,
      });
      onDone();
    } catch (e) {
      setRestoreResult({ ok: false, msg: e?.response?.data?.detail || 'Request failed.' });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="mt-6 border-t border-slate-200 pt-4">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
      >
        <HiCog className="w-4 h-4" />
        Admin — Data Recovery
        <span className="text-slate-300 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* ── Fix OCR-complete docs ─────────────────────────────────────── */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-800 mb-1">
              Fix docs stuck in Processing (have OCR text)
            </p>
            <p className="text-xs text-amber-700 mb-3">
              Affects docs re-queued by ETag churn: sets status=completed,
              ocr_status=completed and queues ES re-index.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleFixOcr}
                disabled={fixing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-white hover:bg-amber-100 border border-amber-300 rounded-lg disabled:opacity-50 transition-colors"
              >
                <HiCog className={`w-4 h-4 ${fixing ? 'animate-spin' : ''}`} />
                {fixing ? 'Fixing…' : 'Fix OCR-Complete Docs'}
              </button>
              {fixResult && (
                <span className={`text-xs font-medium ${fixResult.ok ? 'text-emerald-700' : 'text-red-600'}`}>
                  {fixResult.msg}
                </span>
              )}
            </div>
          </div>

          {/* ── Restore false-deleted docs ────────────────────────────────── */}
          <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
            <p className="text-sm font-semibold text-violet-800 mb-1">
              Restore false-deleted documents
            </p>
            <p className="text-xs text-violet-700 mb-3">
              Restores docs soft-deleted in a time window.
              The next S3 poll will re-delete any that are genuinely gone from S3.
            </p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-xs text-violet-700 w-14 shrink-0">From</label>
                <input
                  type="date"
                  value={deletedAfter}
                  onChange={(e) => setDeletedAfter(e.target.value)}
                  className="input-field text-xs py-1 flex-1 min-w-0"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-xs text-violet-700 w-14 shrink-0">To (opt)</label>
                <input
                  type="date"
                  value={deletedBefore}
                  onChange={(e) => setDeletedBefore(e.target.value)}
                  className="input-field text-xs py-1 flex-1 min-w-0"
                />
              </div>
              <div className="flex items-center gap-3 mt-1">
                <button
                  onClick={handleRestore}
                  disabled={restoring || !deletedAfter}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-violet-700 bg-white hover:bg-violet-100 border border-violet-300 rounded-lg disabled:opacity-50 transition-colors"
                >
                  <HiReply className={`w-4 h-4 ${restoring ? 'animate-spin' : ''}`} />
                  {restoring ? 'Restoring…' : 'Restore Deleted'}
                </button>
                {restoreResult && (
                  <span className={`text-xs font-medium ${restoreResult.ok ? 'text-emerald-700' : 'text-red-600'}`}>
                    {restoreResult.msg}
                  </span>
                )}
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

// ─── ActionBtn ────────────────────────────────────────────────────────────────

const ActionBtn = ({ onClick, loading, loadingLabel, label, colorClass, icon: Icon }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded border transition-colors disabled:opacity-50 ${colorClass}`}
  >
    <Icon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
    {loading ? loadingLabel : label}
  </button>
);

// ─── Tab: Processing Queue ────────────────────────────────────────────────────

const PAGE_SIZE_QUEUE = 20;

const ProcessingQueue = () => {
  // Labels resolve through the catalog so a newly added type renders with its
  // real name rather than a raw key.
  const { labelOf } = useDocumentTypes();
  // Throughput, not just depth. The five counters below are all snapshots: the
  // same "1,800 queued" reads identically whether OCR is working through the
  // backlog or has stopped. The rate is what distinguishes them, and this page
  // is where someone comes to ask that question.
  const [throughput, setThroughput] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [allDocs, setAllDocs]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [sourceFilter, setSourceFilter] = useState('');
  const [ocrFilter, setOcrFilter]       = useState('');
  const [stuckThreshold, setStuckThreshold] = useState(120); // minutes
  const [page, setPage]                 = useState(1);
  const [actionLoading, setActionLoading] = useState({});  // { docId: 'index' | 'retry' | 'fail' }
  const [actionResult, setActionResult] = useState(null);
  const [retrying, setRetrying]         = useState(false);
  const [failingStuck, setFailingStuck] = useState(false);

  // Fetch all processing docs at once (up to 200) for accurate stats + client-side filtering
  const fetchProcessing = useCallback(async () => {
    try {
      const data = await docService.getAll({
        status: 'processing',
        // The API caps size at 100 (le=100). Asking for 200 was rejected with a
        // 422, and the catch below discarded it — so this tab silently showed
        // nothing at all rather than reporting that the request had failed.
        size: 100,
        page: 1,
        sort_by: 'created_at',
        sort_order: 'asc',
      });
      setAllDocs(data.items || []);
      setLoadError(null);
    } catch (err) {
      // A swallowed error here is indistinguishable from an empty queue. Say so.
      setLoadError(apiError(err, 'Could not load the processing queue'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProcessing();
    const interval = setInterval(fetchProcessing, 15000);
    return () => clearInterval(interval);
  }, [fetchProcessing]);

  useEffect(() => {
    analyticsService
      .getActivity()
      .then((d) => setThroughput(d?.pipeline_health ?? null))
      .catch(() => setThroughput(null));   // a missing rate must not break the queue view
  }, []);

  useEffect(() => { setPage(1); }, [sourceFilter, ocrFilter, stuckThreshold]);

  // Stats computed from all docs (ignores current filter)
  const stats = useMemo(() => {
    const now = Date.now();
    let queued = 0, ocrActive = 0, awaitingIndex = 0, stuck = 0;
    allDocs.forEach((d) => {
      const elapsedMin = (now - new Date(d.updated_at || d.created_at).getTime()) / 60000;
      if (d.ocr_status === 'completed') {
        awaitingIndex++;
      } else if (d.ocr_status === 'processing') {
        ocrActive++;
        if (elapsedMin > stuckThreshold) stuck++;
      } else {
        queued++;
        if (elapsedMin > stuckThreshold) stuck++;
      }
    });
    return { total: allDocs.length, queued, ocrActive, awaitingIndex, stuck };
  }, [allDocs, stuckThreshold]);

  // Filtered + paginated docs
  const filteredDocs = useMemo(() =>
    allDocs.filter((d) =>
      (!sourceFilter || d.source_type === sourceFilter) &&
      (!ocrFilter    || d.ocr_status  === ocrFilter)
    ),
    [allDocs, sourceFilter, ocrFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredDocs.length / PAGE_SIZE_QUEUE));
  const pagedDocs  = filteredDocs.slice((page - 1) * PAGE_SIZE_QUEUE, page * PAGE_SIZE_QUEUE);

  // ─── Action helpers ─────────────────────────────────────────────────────────

  const runAction = async (docId, type, fn) => {
    setActionLoading((prev) => ({ ...prev, [docId]: type }));
    setActionResult(null);
    try {
      await fn();
      await fetchProcessing();
    } catch (e) {
      setActionResult({ ok: false, msg: e?.response?.data?.detail || 'Action failed.' });
    } finally {
      setActionLoading((prev) => {
        const next = { ...prev };
        delete next[docId];
        return next;
      });
    }
  };

  const handleForceIndex   = (id) => runAction(id, 'index', () => docService.forceIndex(id));
  const handleRetryOCR     = (id) => runAction(id, 'retry', () => docService.retryProcessing(id));
  const handleForceFailOne = async (doc) => {
    if (!window.confirm(`Force-fail "${doc.filename}"?\n\nThe file will be marked FAILED immediately and the Celery watchdog will stop retrying it.`)) return;
    runAction(doc.id, 'fail', () => docService.forceFailOne(doc.id));
  };

  const handleRetryStuck = async () => {
    setRetrying(true);
    setActionResult(null);
    try {
      const r = await docService.retryStuck(stuckThreshold);
      setActionResult({ ok: true, msg: `Re-queued: ${r.requeued_ocr} OCR + ${r.requeued_index} index task(s)` });
      await fetchProcessing();
    } catch {
      setActionResult({ ok: false, msg: 'Failed to retry stuck documents.' });
    } finally {
      setRetrying(false);
    }
  };

  const handleFailStuck = async () => {
    if (!window.confirm('Mark all documents stuck for over 7 days as FAILED? This cannot be undone.')) return;
    setFailingStuck(true);
    setActionResult(null);
    try {
      const r = await docService.failStuck(7);
      setActionResult({ ok: true, msg: `Marked ${r.failed_count} document(s) as FAILED (stuck >7d)` });
      await fetchProcessing();
    } catch {
      setActionResult({ ok: false, msg: 'Failed to force-fail stuck documents.' });
    } finally {
      setFailingStuck(false);
    }
  };

  // ─── Threshold label helper ─────────────────────────────────────────────────

  const thresholdLabel = stuckThreshold >= 60
    ? `${stuckThreshold / 60}h`
    : `${stuckThreshold}m`;

  return (
    <div className="flex-1 min-h-0 flex flex-col">

      {/* ── Stats Strip ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        {[
          { label: 'Total',          value: stats.total,          color: 'slate'  },
          { label: 'Queued',         value: stats.queued,         color: 'slate'  },
          { label: 'OCR Active',     value: stats.ocrActive,      color: 'blue'   },
          { label: 'Awaiting Index', value: stats.awaitingIndex,  color: 'violet' },
          { label: `Stuck >${thresholdLabel}`, value: stats.stuck, color: stats.stuck > 0 ? 'red' : 'slate' },
          {
            label: throughput?.completed_last_hour ? 'Done last hour' : 'Not moving',
            value: throughput?.completed_last_hour ?? 0,
            color: throughput?.completed_last_hour ? 'emerald' : 'slate',
          },
        ].map(({ label, value, color }) => {
          const isRed = color === 'red' && value > 0;
          return (
            <div key={label} className={`rounded-xl border p-3 ${
              isRed                 ? 'bg-red-50 border-red-200' :
              color === 'blue'      ? 'bg-blue-50 border-blue-200' :
              color === 'violet'    ? 'bg-violet-50 border-violet-200' :
              color === 'emerald'   ? 'bg-emerald-50 border-emerald-200' :
                                      'bg-slate-50 border-slate-200'
            }`}>
              <p className={`text-2xl font-bold ${
                isRed              ? 'text-red-700' :
                color === 'blue'   ? 'text-blue-700' :
                color === 'violet' ? 'text-violet-700' :
                color === 'emerald'? 'text-emerald-700' :
                                     'text-slate-700'
              }`}>{value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{label}</p>
            </div>
          );
        })}
      </div>

      {loadError && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-50 ring-1 ring-red-100 text-red-700">
          {loadError}
        </div>
      )}

      {/* ── Toolbar ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select
          className="input-field w-auto text-sm"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
        >
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          className="input-field w-auto text-sm"
          value={ocrFilter}
          onChange={(e) => setOcrFilter(e.target.value)}
        >
          <option value="">All Steps</option>
          <option value="pending">Queued</option>
          <option value="processing">OCR Active</option>
          <option value="completed">Awaiting Index</option>
          <option value="failed">Failed</option>
        </select>

        <select
          className="input-field w-auto text-sm"
          value={stuckThreshold}
          onChange={(e) => setStuckThreshold(Number(e.target.value))}
        >
          {THRESHOLD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>Stuck if idle &gt; {o.label}</option>
          ))}
        </select>

        <span className="text-sm text-slate-400">
          Auto-refresh 15s &nbsp;·&nbsp;
          <strong className="text-slate-600">{filteredDocs.length}</strong>/{stats.total} shown
        </span>

        <div className="ml-auto flex items-center gap-3">
          {actionResult && (
            <span className={`text-sm font-medium ${actionResult.ok ? 'text-emerald-600' : 'text-red-600'}`}>
              {actionResult.msg}
            </span>
          )}
          <button
            onClick={handleRetryStuck}
            disabled={retrying}
            title={`Re-queue all documents idle for more than ${thresholdLabel}`}
            className="flex items-center gap-2 px-4 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-medium rounded-lg border border-amber-200 transition-colors disabled:opacity-50"
          >
            <HiRefresh className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
            Retry All Stuck
          </button>
          <button
            onClick={handleFailStuck}
            disabled={failingStuck}
            title="Permanently mark as FAILED all documents stuck for over 7 days"
            className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-medium rounded-lg border border-red-200 transition-colors disabled:opacity-50"
          >
            <HiExclamationCircle className={`w-4 h-4 ${failingStuck ? 'animate-spin' : ''}`} />
            Force Fail &gt;7d
          </button>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      {loading ? (
        <p className="text-slate-500 py-8 text-center">Loading...</p>
      ) : pagedDocs.length === 0 ? (
        <div className="py-16 text-center">
          <HiCheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No documents match the current filter</p>
        </div>
      ) : (
        <>
          <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-slate-100">
            <table className={`${TABLE} text-sm min-w-[900px]`} style={TABLE_STYLE}>
              <thead>
                <tr>
                  <th className={`${TH} ${HEADER_RULE} sticky left-0 top-0 ${Z_HEADER_PINNED} bg-white ${STICKY_LEFT_EDGE}`}>
                    Filename
                  </th>
                  {['Type / Source', 'Step', 'Started'].map((h) => (
                    <th key={h} className={`${TH} ${HEADER_RULE} sticky top-0 ${Z_HEADER} bg-white`}>{h}</th>
                  ))}
                  <th
                    className={`${TH} ${HEADER_RULE} sticky top-0 ${Z_HEADER} bg-white cursor-help`}
                    title="Time since the last status update. A high value means the document is stuck."
                  >
                    Idle
                  </th>
                  <th className={`${TH} ${HEADER_RULE} sticky top-0 ${Z_HEADER} bg-white`}>Error</th>
                  <th className={`${TH} ${HEADER_RULE} sticky right-0 top-0 ${Z_HEADER_PINNED} bg-white text-right ${STICKY_RIGHT_EDGE}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagedDocs.map((doc) => {
                  const elapsed        = calcElapsed(doc.updated_at || doc.created_at);
                  const isAwaitingIdx  = doc.ocr_status === 'completed';
                  const isStuck        = !isAwaitingIdx && elapsed.mins > stuckThreshold;
                  const isWarning      = !isAwaitingIdx && !isStuck && elapsed.mins > stuckThreshold / 2;
                  const step           = STEP_BADGE[doc.ocr_status] || STEP_BADGE.pending;
                  const tone           = queueRowTone(isStuck, isWarning);
                  const aLoading       = actionLoading[doc.id];

                  return (
                    <tr key={doc.id} className={`group transition-colors ${tone.row}`}>
                      {/* Filename — pinned left */}
                      <td
                        className={`px-4 py-3 font-medium text-slate-800 sticky left-0 ${Z_BODY_PINNED} ${ROW_RULE} ${tone.pinned} ${STICKY_LEFT_EDGE}`}
                        style={{ minWidth: 220, maxWidth: 260 }}
                      >
                        <span className="block truncate" title={doc.filename}>
                          {doc.filename}
                        </span>
                      </td>

                      {/* Type / Source */}
                      <td className={`px-4 py-3 ${ROW_RULE} whitespace-nowrap`}>
                        <span className="block text-xs text-slate-600">
                          {labelOf(doc.document_type)}
                        </span>
                        <span className="block text-xs text-slate-400">
                          {SOURCE_LABELS[doc.source_type] || doc.source_type}
                        </span>
                      </td>

                      {/* Step */}
                      <td className={`px-4 py-3 ${ROW_RULE} whitespace-nowrap`}>
                        <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                          <span
                            className="w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ backgroundColor: step.dot }}
                            aria-hidden="true"
                          />
                          {step.label}
                        </span>
                      </td>

                      {/* Started (created_at = when entered pipeline) */}
                      <td className={`px-4 py-3 ${ROW_RULE} text-xs text-slate-500 whitespace-nowrap`}>
                        {formatDateTime(doc.created_at)}
                      </td>

                      {/* Idle (time since last update) */}
                      <td className={`px-4 py-3 ${ROW_RULE} text-xs whitespace-nowrap font-medium`}>
                        <span className={
                          isStuck   ? 'text-red-600' :
                          isWarning ? 'text-amber-600' :
                                      'text-slate-500'
                        }>
                          {elapsed.text}
                          {isStuck && ' ⚠'}
                        </span>
                      </td>

                      {/* Error */}
                      <td className={`px-4 py-3 ${ROW_RULE} text-red-600 text-xs max-w-[200px]`}>
                        {doc.processing_error
                          ? <span className="block truncate" title={doc.processing_error}>{doc.processing_error}</span>
                          : <span className="text-slate-300">—</span>
                        }
                      </td>

                      {/* Actions — pinned right */}
                      <td className={`px-4 py-3 sticky right-0 ${Z_BODY_PINNED} ${ROW_RULE} ${tone.pinned} ${STICKY_RIGHT_EDGE}`}>
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          {isAwaitingIdx && (
                            <ActionBtn
                              onClick={() => handleForceIndex(doc.id)}
                              loading={aLoading === 'index'}
                              loadingLabel="Queuing…"
                              label="Index Now"
                              icon={HiLightningBolt}
                              colorClass="text-blue-600 bg-blue-50 hover:bg-blue-100 border-blue-200"
                            />
                          )}
                          {isStuck && (
                            <ActionBtn
                              onClick={() => handleRetryOCR(doc.id)}
                              loading={aLoading === 'retry'}
                              loadingLabel="Retrying…"
                              label="Retry OCR"
                              icon={HiRefresh}
                              colorClass="text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200"
                            />
                          )}
                          {(isStuck || isWarning) && (
                            <ActionBtn
                              onClick={() => handleForceFailOne(doc)}
                              loading={aLoading === 'fail'}
                              loadingLabel="Failing…"
                              label="Force Fail"
                              icon={HiExclamationCircle}
                              colorClass="text-red-600 bg-red-50 hover:bg-red-100 border-red-200"
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-100">
            <span className="text-sm text-slate-500">
              {filteredDocs.length} documents · Page {page} of {totalPages}
            </span>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </>
      )}

      <RecoveryPanel onDone={fetchProcessing} />
    </div>
  );
};

// ─── Tab: Sync Logs ───────────────────────────────────────────────────────────

const PAGE_SIZE_LOGS = 20;

const SyncLogs = () => {
  const [logError, setLogError] = useState(null);
  const [allLogs, setAllLogs]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage]                 = useState(1);

  const fetchLogs = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (sourceFilter) params.source_type = sourceFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await syncService.getLogs(params);
      setAllLogs(data);
      setLogError(null);
      setPage(1);
    } catch (err) {
      setLogError(apiError(err, 'Could not load sync logs'));
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, statusFilter]);

  useEffect(() => {
    setLoading(true);
    fetchLogs();
    const interval = setInterval(fetchLogs, 3600000); // auto-refresh every 1h
    return () => clearInterval(interval);
  }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(allLogs.length / PAGE_SIZE_LOGS));
  const pagedLogs  = allLogs.slice((page - 1) * PAGE_SIZE_LOGS, page * PAGE_SIZE_LOGS);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {logError && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-50 ring-1 ring-red-100 text-red-700">
          {logError}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select
          className="input-field w-auto text-sm"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
        >
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="input-field w-auto text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-slate-400">
            {allLogs.length} logs &nbsp;·&nbsp; Auto-refreshes every 1h
          </span>
          <button
            onClick={() => { setLoading(true); fetchLogs(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
          >
            <HiRefresh className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-slate-500 py-8 text-center">Loading...</p>
      ) : pagedLogs.length === 0 ? (
        <div className="py-16 text-center">
          <HiDatabase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No sync logs found</p>
        </div>
      ) : (
        <>
          <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-slate-100">
            <table className={`${TABLE} text-sm`} style={TABLE_STYLE}>
              <thead>
                <tr>
                  {['Time', 'Source', 'Event', 'Status', 'Duration', 'Path / summary'].map((h) => (
                    <th key={h} className={`${TH} ${HEADER_RULE} sticky top-0 ${Z_HEADER} bg-white`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                    <td className={`px-4 py-3 ${ROW_RULE} text-slate-500 whitespace-nowrap tabular-nums`}>
                      {new Date(log.processed_at).toLocaleString()}
                    </td>
                    <td className={`px-4 py-3 ${ROW_RULE} text-slate-700 whitespace-nowrap`}>
                      {SOURCE_LABELS[log.source_type] || log.source_type}
                    </td>
                    <td className={`px-4 py-3 ${ROW_RULE} text-slate-600 whitespace-nowrap`}>
                      {log.event_type || '—'}
                    </td>
                    <td className={`px-4 py-3 ${ROW_RULE} whitespace-nowrap`}>
                      <span className="inline-flex items-center gap-1.5 text-slate-700">
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: LOG_STATUS_DOT[log.status] ?? '#898781' }}
                          aria-hidden="true"
                        />
                        {log.status || '—'}
                      </span>
                    </td>
                    <td className={`px-4 py-3 ${ROW_RULE} text-slate-500 whitespace-nowrap tabular-nums`}>
                      {formatDuration(log.processing_duration_ms)}
                    </td>
                    <td className={`px-4 py-3 ${ROW_RULE} max-w-xs`}>
                      <SyncSummaryText text={log.error_message} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-100">
            <span className="text-sm text-slate-500">
              Showing {(page - 1) * PAGE_SIZE_LOGS + 1}–{Math.min(page * PAGE_SIZE_LOGS, allLogs.length)} of {allLogs.length}
            </span>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </>
      )}
    </div>
  );
};

// ─── S3 Last-Sync Summary ─────────────────────────────────────────────────────

const SyncSummary = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    syncService.getStatus().then(setStatus).catch(() => {});
  }, []);

  if (!status) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {Object.entries(status.last_sync).map(([source, time]) => (
        <div key={source} className="card flex items-center gap-4 py-4">
          <div className={`p-2.5 rounded-full ${time ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
            <HiRefresh className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800 capitalize">{source.replace(/_/g, ' ')}</p>
            <p className="text-xs text-slate-500">
              {time ? new Date(time).toLocaleString() : 'Never synced'}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'processing', label: 'Processing Queue', icon: HiClock },
  { id: 'logs',       label: 'Sync Logs',        icon: HiDatabase },
];

const SyncStatusPage = () => {
  const [activeTab, setActiveTab] = useState('processing');

  return (
    <div className="page-container h-full flex flex-col">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Sync Status</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Documents moving through OCR and indexing, and the history of each poll
        </p>
      </div>

      <SyncSummary />

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6 flex gap-6">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            aria-current={activeTab === id ? 'page' : undefined}
            className={`flex items-center gap-2 pb-3 -mb-px text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? 'border-blue-600 text-slate-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      <div className="card p-0 overflow-hidden flex-1 min-h-[18rem] flex flex-col">
        <div className="p-6 flex-1 min-h-0 flex flex-col">
          {activeTab === 'processing' && <ProcessingQueue />}
          {activeTab === 'logs'       && <SyncLogs />}
        </div>
      </div>
    </div>
  );
};

export default SyncStatusPage;
